import { useMediaQuery, useTheme } from '@mui/material';
import Card, { ButtonVariantType, GridAlignType, ImagePositionType } from '../shared/Card/Card';
import UiContainer from '../shared/Container/Container';
import UiHeading from '../shared/Heading';
import UiText from '../shared/Typography/UiText';
import UiSpacer from '../shared/Spacer/Spacer';
import UiBox from '../shared/Box';
import React from 'react';
import { LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import Carousel from '../shared/CarouselV2/Carousel';

enum Layout {
  Stack = 'Stack',
  Carousel = 'Carousel',
}

export type LayoutType = keyof typeof Layout;

type Props = {
  title?: React.ReactNode;
  description?: React.ReactNode | null;
  columnSize?: number | null;
  cards: CardItem[];
  layout: LayoutType;
  buttonAlign?: GridAlignType;
  buttonFullWidth?: boolean;
  imageHeight?: string;
  cardBorderRadius?: string;
  imageBorderRadius?: string;
  background?: string;
};

type CardItem = {
  title?: React.ReactElement | null;
  description?: React.ReactElement | null;
  image?: React.ReactElement | null;
  label?: React.ReactElement | null;
  metaText?: React.ReactElement | null;
  button?: LinkField;
  fullWidth?: boolean;
  removeImagePadding?: boolean;
  imagePosition?: ImagePositionType;
  buttonVariant?: ButtonVariantType;
};

const DEFAULT_LAYOUT: LayoutType = 'Stack';
const DEFAULT_COL_SIZE = 3;
const MAX_COL_SIZE = 4;
const DEFAULT_IMAGE_HEIGHT = '276px';

function SectionWithCardList({
  title,
  description,
  cards,
  columnSize,
  layout = DEFAULT_LAYOUT,
  imageHeight = DEFAULT_IMAGE_HEIGHT,
  cardBorderRadius,
  imageBorderRadius,
  background,
  buttonAlign,
  buttonFullWidth,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCarousel = Layout[layout] === Layout.Carousel;

  if (!columnSize || columnSize > MAX_COL_SIZE) {
    columnSize = DEFAULT_COL_SIZE;
  }
  if (isMobile && isCarousel) {
    columnSize = 1;
  }

  return (
    <UiBox
      styles={{ background }}
      id="ui-card-list-section"
      className="ui-card-list-section-wrapper"
      data-testid="section"
    >
      <UiContainer>
        <UiSpacer size="xl" />
        <UiHeading className="ui-card-list-title" size="h2" align="center" isChildren>
          {title}
        </UiHeading>
        <UiSpacer size="sm" />
        {description && (
          <>
            <UiText color="default">{description}</UiText>
            <UiSpacer size="lg" />
          </>
        )}
        <Carousel columnSize={columnSize} disable={layout === 'Stack'}>
          {cards.map((card, key) => (
            <Carousel.Item key={key} columnSize={columnSize} fullWidth={card.fullWidth}>
              <Card
                removeImagePadding={card.removeImagePadding}
                height="100%"
                background="white"
                borderRadius={cardBorderRadius}
              >
                {{
                  image: card.image && (
                    <Card.Image
                      height={imageHeight}
                      borderRadius={imageBorderRadius}
                      position={card.imagePosition}
                    >
                      {card.image}
                    </Card.Image>
                  ),
                  label: card.label && <Card.Label>{card.label}</Card.Label>,
                  title: card.title && <Card.Title>{card.title}</Card.Title>,
                  description: card.description && (
                    <Card.Description>{card.description}</Card.Description>
                  ),
                  metaText: card.metaText && (
                    <Card.Label size="caption">{card.metaText}</Card.Label>
                  ),
                  button: card.button && (
                    <Card.Button
                      align={buttonAlign}
                      fullWidth={buttonFullWidth}
                      variant={card.buttonVariant}
                    >
                      {card.button}
                    </Card.Button>
                  ),
                }}
              </Card>
            </Carousel.Item>
          ))}
        </Carousel>
        <UiSpacer size="xl" />
      </UiContainer>
    </UiBox>
  );
}

export default SectionWithCardList;
export type { CardItem };
